import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { createEntityAdapter, Dictionary, EntityState } from '@reduxjs/toolkit';

import createSafeContext from '#/utils/createSafeContext';
import { MarketSummaryNotification } from '#/api/ws/ws';
import useLiveConnection, {
  LiveConnectionBaseState,
} from '#/utils/useLiveConnection';
import {
  MarketsState,
  usePerpetualMarketsState,
} from '#/features/perpetuals/market-info/markets-context';
import {
  TradeActiveMarketStoreView,
  useTradeActiveMarketStoreView,
} from '#/features/perpetuals/active-market-context';
import * as MarketFn from '#/features/market/functions';
import {
  PerpetualMarketSummary,
  GetMarketsSummaryResp,
  getMarketsSummary,
} from '#/api/markets-summary';
import { Market } from '#/api/markets';

const selectId = (market: PerpetualMarketSummary) => market.symbol;

const adapter = createEntityAdapter<PerpetualMarketSummary>({
  selectId,
});
const selectors = adapter.getSelectors();

const INITIAL_STATE: MarketsSummaryState = {
  status: 'loading',
  error: '',
  marketsSummary: adapter.getInitialState(),
};

export interface MarketsSummaryState extends LiveConnectionBaseState {
  readonly marketsSummary: EntityState<PerpetualMarketSummary>;
}

export interface MarketsSummaryStoreView {
  readonly status: MarketsSummaryState['status'];
  readonly error: MarketsSummaryState['error'];
  readonly getMarketSummary: (
    id: PerpetualMarketSummary['symbol'],
  ) => PerpetualMarketSummary | undefined;
}

export interface MarketsSummaryView
  extends Omit<MarketsSummaryState, 'marketsSummary'> {
  readonly activeMarketSummary: MarketSummaryView;
}

export const [useMarketsSummaryState, MarketsSummaryStateCtx] =
  createSafeContext<MarketsSummaryState>('MarketsSummaryState');
export const [useMarketsSummaryStoreView, MarketsSummaryStoreViewProvider] =
  createSafeContext<MarketsSummaryStoreView>('MarketsSummaryStoreView');
export const [useMarketsSummaryView, MarketsSummaryViewCtx] =
  createSafeContext<MarketsSummaryView>('MarketsSummaryView');

export function useActiveMarketSummaryState():
  | PerpetualMarketSummary
  | undefined {
  const { activeMarket } = useTradeActiveMarketStoreView();
  const { marketsSummary } = useMarketsSummaryState();

  if (activeMarket == null) return undefined;

  return marketsSummary.entities[activeMarket.symbol];
}

interface ProviderProps {
  readonly children: ReactNode;
}

export function MarketsSummaryStateProvider(props: ProviderProps) {
  const [state, setState] = useState(INITIAL_STATE);

  const storeView = useMemo(() => createStoreView(state), [state]);

  useMarketsSummaryLiveConnection(setState);

  return (
    <MarketsSummaryStateCtx value={state}>
      <MarketsSummaryStoreViewProvider value={storeView}>
        {props.children}
      </MarketsSummaryStoreViewProvider>
    </MarketsSummaryStateCtx>
  );
}

export function MarketsSummaryViewProvider(props: ProviderProps) {
  const marketsSummaryState = useMarketsSummaryState();
  const marketsState = usePerpetualMarketsState();
  const activeMarketStoreView = useTradeActiveMarketStoreView();

  const view = useMemo(
    () => createView(marketsSummaryState, marketsState, activeMarketStoreView),
    [marketsSummaryState, marketsState, activeMarketStoreView],
  );

  return (
    <MarketsSummaryViewCtx value={view}>{props.children}</MarketsSummaryViewCtx>
  );
}

export function createStoreView(
  state: MarketsSummaryState,
): MarketsSummaryStoreView {
  const getMarketSummary = (id: PerpetualMarketSummary['symbol']) =>
    selectors.selectById(state.marketsSummary, id);

  return {
    status: state.status,
    error: state.error,
    getMarketSummary,
  };
}

export interface MarketSummaryView {
  readonly symbol: string;
  readonly volume24h: string;
  readonly oraclePrice: string;
  readonly openInterest: string;
  readonly underlyingPrice: string;
  readonly fundingRate8h: string;

  // Derived
  readonly fundingRateChangeDirection: 'up' | 'down' | null;
}

export function createView(
  marketsSummaryState: MarketsSummaryState,
  marketsState: MarketsState,
  activeMarketStoreView: TradeActiveMarketStoreView,
): MarketsSummaryView {
  if (marketsState.status !== 'done') {
    return {
      status: marketsState.status,
      error: marketsState.error,
      activeMarketSummary: emptyMarketSummaryView(),
    };
  }

  const activeMarketSummary = activeMarketSummaryView(
    marketsSummaryState,
    marketsState,
    activeMarketStoreView,
  );

  return {
    // WIP `marketsSummary` should not be part of the view and should
    // not be used in other contexts. It should instead be provided as
    // part of a "store view". See other "store contexts" for examples.
    ...marketsSummaryState,
    activeMarketSummary,
  };
}

const emptyMarketSummaryView = (
  marketSummary?: Pick<PerpetualMarketSummary, 'symbol'>,
): MarketSummaryView => ({
  symbol: marketSummary?.symbol ?? '-',
  volume24h: '-',
  oraclePrice: '-',
  openInterest: '-',
  underlyingPrice: '-',
  fundingRate8h: '-',
  fundingRateChangeDirection: null,
});

function activeMarketSummaryView(
  marketsSummaryState: MarketsSummaryState,
  marketsState: MarketsState,
  activeMarketStoreView: TradeActiveMarketStoreView,
) {
  const { activeMarket } = activeMarketStoreView;
  const marketsSummary = selectors.selectEntities(
    marketsSummaryState.marketsSummary,
  );

  if (activeMarket == null) return emptyMarketSummaryView();

  const activeMarketSummary = marketsSummary[activeMarket.symbol];
  if (activeMarketSummary == null) return emptyMarketSummaryView();

  return marketSummaryView(activeMarketSummary, marketsState.markets.entities);
}

function marketSummaryView(
  marketSummary: PerpetualMarketSummary,
  markets: Dictionary<Market>,
): MarketSummaryView {
  const market = markets[marketSummary.symbol];

  if (market == null) return emptyMarketSummaryView(marketSummary);

  const quoteCurrencyDecimalPlaces =
    MarketFn.quoteCurrencyDecimalPlaces(market);

  const volume24h = `$${marketSummary.volume_24h.toFormat(0)}`;

  const oraclePrice =
    marketSummary.oracle_price != null
      ? `$${marketSummary.oracle_price.toFormat(quoteCurrencyDecimalPlaces)}`
      : '-';

  const openInterest =
    marketSummary.oracle_price != null
      ? `$${marketSummary.open_interest
          .times(marketSummary.oracle_price)
          .toFormat(0)}`
      : '-';

  const underlyingPrice = `$${marketSummary.underlying_price.toFormat(
    quoteCurrencyDecimalPlaces,
  )}`;

  const fundingRate8h = fundingRate8hView(marketSummary);

  const fundingRateChangeDirection = marketSummary.funding_rate.isPositive()
    ? 'up'
    : 'down';

  return {
    symbol: marketSummary.symbol,
    volume24h,
    oraclePrice,
    openInterest,
    underlyingPrice,
    fundingRate8h,
    fundingRateChangeDirection,
  };
}

function fundingRate8hView(marketSummary: PerpetualMarketSummary) {
  /** Given `funding_rate` is 24h funding rate, we need to divide it by 3 to get 8h funding rate. */
  return `${marketSummary.funding_rate.times(100).dividedBy(3).toFormat(4)}%`;
}

function useMarketsSummaryLiveConnection(
  setState: Dispatch<SetStateAction<MarketsSummaryState>>,
): void {
  const channel = `markets_summary`;

  const fetchAll = useCallback(
    async (signal: AbortSignal) =>
      getMarketsSummary({
        market: 'ALL',
        signal,
      }),
    [],
  );

  const setAll = useCallback(
    (data: GetMarketsSummaryResp) => {
      setState((state) => ({
        ...state,
        marketsSummary: adapter.setAll(state.marketsSummary, data.results),
      }));
    },
    [setState],
  );

  const resetAll = useCallback(() => {
    setState((state) => ({
      ...state,
      marketsSummary: adapter.removeAll(state.marketsSummary),
    }));
  }, [setState]);

  const applyWSNotification = useCallback(
    (notif: MarketSummaryNotification) => {
      setState((state) => ({
        ...state,
        marketsSummary: adapter.setOne(state.marketsSummary, notif.data),
      }));
    },
    [setState],
  );

  useLiveConnection<
    GetMarketsSummaryResp,
    MarketsSummaryState,
    MarketSummaryNotification
  >({
    setState,
    channel,
    fetchAll,
    setAll,
    resetAll,
    applyWSNotification,
  });
}
